import React from 'react'
import PropTypes from 'prop-types'
import styled, { withTheme } from 'styled-components'
import { themeColor, themeFontSize } from '../../util/themeLenses'
import {
    fontSize,
    fontWeight,
    justifyContent,
    width,
    height,
    padding,
    borderRadius,
    borderWidth,
    borderStyle,
    space,
} from '../../util/styles'
import Icon from '../Icon'
import Flex from '../Flex'
import { injectIntl } from 'react-intl'

const containerBorderColor = ({ valid, changed, errorColor }) => !valid && changed ? themeColor(errorColor) : '0'
const containerBorderWidth = ({ valid, changed }) => !valid && changed ? `2px` : '0'
const containerPadding = ({ valid, changed }) => !valid && changed ? '8px' : '0'
const DEFAULT_BOX_SIZE = '16px'

const fontColorLabelCheckbox = ({fontColor, theme}) => themeColor(fontColor || theme.components.checks.labelColor)

export const CheckboxInputContainer = styled.div.attrs(props => ({'data-test': props.dataTest}))`
    border-color: ${containerBorderColor};
    border-style: solid;
    border-width: ${containerBorderWidth};
    padding: ${containerPadding};
    border-radius: 2px;
    ${justifyContent};
    line-height: ${(props) => props.lineHeight};

    input:checked + label {
        color: ${fontColorLabelCheckbox};
    }
    ${width};
    `

CheckboxInputContainer.propTypes = {
    errorColor: PropTypes.string,
    valid: PropTypes.bool,
    changed: PropTypes.bool,
}

CheckboxInputContainer.defaultProps = {
    errorColor: 'red',
    width: 'auto'
}



export const CheckboxLabel = styled.label`
    display: flex;
    align-items: center;
    ${fontSize};
    color: ${fontColorLabelCheckbox};
    ${fontWeight};
    ${justifyContent};
    cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
    `

CheckboxLabel.propTypes = {
    fontColor: PropTypes.string,
    fontSize: PropTypes.string,
    fontWeight: PropTypes.string,
}
const checkedBackgroundColor = ({theme}) => themeColor(theme.components.checks.checkedColor)

const StyledCheckButton = styled.div`
    flex: 0 0 auto;
    display: inline-block;
    width: ${({elementSize}) => elementSize};
    height: ${({elementSize}) => elementSize};
    border-radius: 50%;
    border: 1px solid ${themeColor('gray60')};
    background: ${themeColor('white')};
    box-sizing: border-box;
    margin-right: 8px;
    position: relative;

    &:after {
        content: ' ';
        display: block;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 0;
        height: 0;
        border-radius: 50%;
        box-sizing: border-box;
        margin-right: 8px;
        background: ${checkedBackgroundColor};
        transition: 0.1s;
    }
    
    input:checked ~ label > &:after {
        width: calc(${({elementSize}) => elementSize} - 6px );
        height: calc(${({elementSize}) => elementSize} - 6px);
    }

    &:focus-visible{
        outline: 1px solid ${themeColor('focusColor')};
    }
`

const CheckButton = (props) => <StyledCheckButton aria-label='' {...props} />
CheckButton.defaultProps = {
    elementSize: DEFAULT_BOX_SIZE,
}

CheckButton.squared = styled(CheckButton)`
    border-radius: 0;
    &:after {
        border-radius: 0;
    }
    border: 1px solid ${({ checkInputBorderColor }) => checkInputBorderColor ? themeColor(checkInputBorderColor) : themeColor('gray60')};
`

CheckButton.toggle = styled(CheckButton)`
    width: 40px;
    height: 24px;
    background: red;
    border-radius: 50px;
    position: relative;
    background: ${themeColor('white')};
    border: 1px solid ${themeColor('gray20')};

    &:after {
        border-radius: 50%;
        border: 1px solid ${themeColor('gray20')};
        top: 50%;
        left: 11px;
        height: 24px;
        width: 24px;
        content: ' ';
        display: block;
        background: ${themeColor('white')};
        position: absolute;
        transition: left ease-out 0.3s;
        box-shadow: 0 1px 3px rgba(0,0,0,.3);
    }

    input:checked ~ label > &:after {
        border: 1px solid ${themeColor('gray20')};
        top: 50%;
        left: calc(100% - 11px);
        height: 24px;
        width: 24px;
    }

    input:checked ~ label > & {
        background: ${checkedBackgroundColor};
    }

`

CheckButton.miniToggle = styled(CheckButton.toggle)`
    width: 28px;
    height: 16px;
    border-radius: 16px;
    background: ${themeColor('lightGray20')};
    &:after {
        left: -1px;
        height: 16px;
        width: 16px;
        box-shadow: none;
    }

    input:checked ~ label > &:after {
        left: 12px;
        height: 16px;
        width: 16px;
    }
`


const fontColorIconFilled = ({fontColor, disabled, theme}) => {

    const iconFilledStyles = theme.components.checks.iconFilled

    if(fontColor) return themeColor(fontColor)

    return disabled ? themeColor(iconFilledStyles.disabledFontColor) : themeColor(iconFilledStyles.defaultFontColor)
}


const borderColorIconFilled = ({borderColor, disabled, theme}) => {

    const iconFilledStyles = theme.components.checks.iconFilled
    if(borderColor) return themeColor(borderColor)

    return disabled ? themeColor(iconFilledStyles.disabledBorderColor) : themeColor(iconFilledStyles.defaultBorderColor)
}

const backgroundColorIconFilled = ({backgroundColor, disabled, theme}) => {

    const iconFilledStyles = theme.components.checks.iconFilled

    if(backgroundColor) return themeColor(backgroundColor)

    return disabled ? themeColor(iconFilledStyles.disabledBackgroundColor) : themeColor(iconFilledStyles.defaultBackgroundColor)
}

const CheckButtonIconFilledContainer = styled(Flex)`
    width: calc(${(props) => themeFontSize(props.fontSize)} + 8px);
    height: calc(${(props) => themeFontSize(props.fontSize)} + 8px);
    border-radius: ${({squared}) => squared ? '4px' : '50%'};
    border-width: 1px;
    border-style: solid;
    align-items: center;
    justify-content: center;
    border-color: ${borderColorIconFilled};
    background: ${backgroundColorIconFilled};
    color: ${fontColorIconFilled};
    margin-right: 8px;

`

const CheckButtonIconFilled = ({ iconType, checked, disabled, fontSize, squared, borderColor, backgroundColor, theme, onKeyDown, ariaLabel, intl }) => {
    ariaLabel = ariaLabel || intl.formatMessage({ defaultMessage: 'Haz click o pulsa enter para marcar o desmarcar esta casilla' })
    return (
        <CheckButtonIconFilledContainer aria-label={ariaLabel} role='checkbox' onKeyDown={onKeyDown} aria-checked={checked} tabIndex={0} disabled={disabled} checked={checked} fontSize={fontSize} squared={squared} borderColor={borderColor} backgroundColor={backgroundColor}>
            {checked && <Icon color={theme.components.checks.iconFilled.iconColor} type={iconType} size={fontSize} />}
        </CheckButtonIconFilledContainer>
    )
}

CheckButtonIconFilled.propTypes = {
    iconType: PropTypes.string,
    checked: PropTypes.bool,
    disabled: PropTypes.bool,
    squared: PropTypes.bool,
    fontSize: PropTypes.string,
    borderColor: PropTypes.string,
    backgroundColor: PropTypes.string,
    theme: PropTypes.object,
    onKeyDown: PropTypes.func,
    intl: PropTypes.object,
    ariaLabel: PropTypes.string,
}

CheckButtonIconFilled.defaultProps = {
    iconType: 'ok',
    checked: false,
    disabled: false,
    fontSize: 'xxSmall',
    squared: false,
    onKeyDown: () => { },
}

CheckButton.iconFilled = withTheme(injectIntl(CheckButtonIconFilled))

const colorLabelInside = ({ borderColor, checked, disabled, theme }) => {

    const labelInsideStyles = theme.components.checks.labelInside
    if (borderColor) {
        return themeColor(borderColor)
    }

    if (disabled) {
        return themeColor(labelInsideStyles.disabledBorderColor)
    }

    return checked ? themeColor(labelInsideStyles.checkedBorderColor) : themeColor(labelInsideStyles.defaultBorderColor)

}

const backgroundColorLabelInside = ({ backgroundColor, checked, disabled, theme }) => {

    const labelInsideStyles = theme.components.checks.labelInside

    if (backgroundColor) {
        return themeColor(backgroundColor)
    }

    if (disabled) {
        return themeColor(labelInsideStyles.disabledBackgroundColor)
    }

    return checked ? themeColor(labelInsideStyles.checkedBackgroundColor) : themeColor(labelInsideStyles.defaultBackgroundColor)

}

const CheckButtonLabelInsideContainer = styled(Flex)`
    ${width};
    ${height};
    ${padding}
    ${borderRadius}
    ${borderWidth}
    ${borderStyle}
    border-color: ${colorLabelInside};
    background-color: ${backgroundColorLabelInside};
    ${space}
`


const CheckButtonLabelInside = ({ checked, disabled, backgroundColor, borderColor, labelComponent, width, height, onKeyDown, ...restProps }) => {
    return (
        <CheckButtonLabelInsideContainer
            onKeyDown={onKeyDown}
            aria-checked={checked}
            role='radio'
            justifyContent='center'
            alignItems='center'
            borderWidth='1px'
            borderStyle='solid'
            backgroundColor={backgroundColor}
            borderColor={borderColor}
            disabled={disabled}
            checked={checked}
            width={width}
            height={width}
            {...restProps}>
           {labelComponent}
        </CheckButtonLabelInsideContainer>
    )
}

CheckButtonLabelInside.propTypes = {
    checked: PropTypes.bool,
    disabled: PropTypes.bool,
    labelComponent: PropTypes.node.isRequired,
    backgroundColor: PropTypes.string,
    borderColor: PropTypes.string,
    width: PropTypes.string,
    height: PropTypes.string,
    onKeyDown: PropTypes.func,
}

CheckButtonLabelInside.defaultProps = {
    checked: false,
    borderRadius: '4px',
    disabled: false,
    width: '100%',
    height: '100%',
    onKeyDown: () => {},
}

CheckButton.labelInside = CheckButtonLabelInside

export { CheckButton }


const TYPES = {
    default: CheckButton,
    squared: CheckButton.squared,
    toggle: CheckButton.toggle,
    miniToggle: CheckButton.miniToggle,
    iconFilled: CheckButton.iconFilled,
    labelInside: CheckButton.labelInside,
}

export { TYPES }

export const CheckboxInput = styled.input`
    display: none;
`
